import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../client/components/ui/dialog';
import { Button } from '../../client/components/ui/button';
import { Textarea } from '../../client/components/ui/textarea';
import { SmilePlus, Meh, Frown, CheckIcon } from 'lucide-react';
import { useTranslation } from '../../i18n/hooks';
import { useAuth } from 'wasp/client/auth';
import { getCollectionById } from 'wasp/client/operations';

const FeedbackDialog: React.FC = () => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [email, setEmail] = useState('');
    const [feedback, setFeedback] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [selectedMood, setSelectedMood] = useState<'happy' | 'neutral' | 'sad' | null>(null);
    const { data: user, isLoading } = useAuth();
    const [collection, setCollection] = useState<any>(null);

    async function getCollection() {
        const collection = await getCollectionById({ id: user?.currentCollectionId || "" });
        setCollection(collection?.name || "");
    }

    useEffect(() => {
        getCollection();
    }, [user?.currentCollectionId]);
  
    const resetForm = () => {
      setEmail('');
      setFeedback('');
      setSelectedMood(null);
      setShowSuccess(false);
    };
  
    const handleSubmit = async () => {
      if (!feedback.trim() || !email.trim()) return;
      
      setIsSubmitting(true);
      try {
        // Convert the data to URLSearchParams to avoid preflight
        const formData = new URLSearchParams();
        formData.append('email', email);
        formData.append('feedback', feedback);
        formData.append('mood', selectedMood || '');
        formData.append('registeredMail', user?.email || '');
        formData.append(
          "storeName",
          user?.miqShopifyDomain || collection || ""
        );
        formData.append('userId', user?.id || '');

        const response = await fetch('https://hooks.zapier.com/hooks/catch/15763147/28i1w52', {
          method: 'POST',
          // Don't set Content-Type header - let the browser set it automatically
          body: formData,
        });
    
        if (response.ok) {
          setShowSuccess(true);
          setTimeout(() => {
            setIsOpen(false);
            resetForm();
          }, 1500);
        }
      } catch (error) {
        console.error(t('feedback.error_message'), error);
      } finally {
        setIsSubmitting(false);
      }
    };
  
    const MoodButton = ({ mood, icon: Icon, selected }: any) => (
      <button
        type="button"
        onClick={() => setSelectedMood(mood)}
        className={`p-2 rounded-full transition-colors ${
          selected 
            ? 'bg-primary text-white' 
            : 'hover:bg-gray-100 text-gray-500'
        }`}
      >
        <Icon className="w-6 h-6" />
      </button>
    );
  
    const isFormValid = email.trim() && feedback.trim();
  
    return (
      <Dialog open={isOpen} onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) resetForm();
      }}>
        <DialogTrigger asChild>
          <button className="hidden lg:block text-white hover:text-gray-300 transition-colors">
            {t('feedback.button')}
          </button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t('feedback.title')}</DialogTitle>
            <DialogDescription>
              {t('feedback.description')}
            </DialogDescription>
          </DialogHeader>
  
          {showSuccess ? (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mb-4">
                <CheckIcon className="w-6 h-6 text-green-600" />
              </div>
              <p className="text-lg text-center">{t('feedback.success_message')}</p>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              <div className="flex justify-center gap-4 mb-6">
                <MoodButton 
                  mood="happy" 
                  icon={SmilePlus} 
                  selected={selectedMood === 'happy'} 
                />
                <MoodButton 
                  mood="neutral" 
                  icon={Meh} 
                  selected={selectedMood === 'neutral'} 
                />
                <MoodButton 
                  mood="sad" 
                  icon={Frown} 
                  selected={selectedMood === 'sad'} 
                />
              </div>
  
              <div className="space-y-2">
                <label 
                  htmlFor="email" 
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  {t('feedback.email_label')}
                </label>
                <input
                  id="email"
                  type="email"
                  placeholder={t('feedback.email_placeholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-800 dark:border-gray-700"
                />
              </div>
  
              <div className="space-y-2">
                <label 
                  htmlFor="feedback" 
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  {t('feedback.feedback_label')}
                </label>
                <Textarea
                  id="feedback"
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder={t('feedback.feedback_placeholder')}
                  className="min-h-[100px]"
                  required
                />
              </div>
  
              <div className="text-sm text-gray-500">
                {t('feedback.support_text')}{' '}
                <a 
                  href="https://www.marketplaceiq.io/docs/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  {t('feedback.support_link')}
                </a>
                {' '}{t('feedback.or')}{' '}
                <a 
                  href="https://www.marketplaceiq.io/docs/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  {t('feedback.docs_link')}
                </a>
                {' '}{t('feedback.instead')}.
              </div>
  
              <div className="pt-4">
                <Button
                  onClick={handleSubmit}
                  disabled={!isFormValid || isSubmitting}
                  className="w-full h-12 text-lg"
                >
                  {isSubmitting ? t('feedback.submitting') : t('feedback.submit')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    );
  };
  
  export default FeedbackDialog;